import { RefreshStatus } from '../components/RefreshStatus';
import { Disclaimer } from '../components/Disclaimer';
import { TestIntro } from '../components/TestIntro';
import { paperTestSummary } from '../lib/paperTestPages';
import { Skeleton } from '../components/Skeleton';
import { useLiquidityRewards } from '../hooks/useLiquidityRewards';
import {
  ApiClientError,
  type LiquidityRewardsResponse,
  type LiquidityTrackedRow,
} from '../lib/apiClient';
import type { FinishedMarket, LipBucketSummary } from '../lib/liquidityRewards';
import { formatSignedUsd } from '../lib/format';
import styles from './CrashBouncePage.module.css';

const usd = (x: number) => `$${x.toFixed(2)}`;
const priceCents = (x: number) => `${Math.round(x * 100)}¢`;
const pct = (x: number | null) =>
  x === null ? '—' : `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(x * 100).toFixed(1)}%`;
const dayTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const VERDICT_LABEL = {
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
} as const;

const BUCKET_TITLE = {
  short: 'Short programs (2 hours or less)',
  long: 'Long programs (over 2 hours)',
} as const;

export function LiquidityRewardsPage() {
  const query = useLiquidityRewards();
  const data = query.data;
  const error =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'Liquidity Rewards data is temporarily unavailable.';
  return (
    <div className={styles.page}>
      <TestIntro
        heading={
          <div className={styles.heading}>
            <h1>Liquidity Rewards</h1>
            <RefreshStatus
              updatedAt={query.dataUpdatedAt}
              isFetching={query.isFetching}
              isError={query.isError}
              onRefresh={() => query.refetch()}
            />
          </div>
        }
        summary={paperTestSummary('/liquidity-rewards')}
      >
        <p>
          Kalshi pays traders to keep orders resting near the top of the book, whether or
          not they fill. That money doesn&apos;t depend on predicting anything. The catch
          is that resting orders get filled mostly by traders who know where the price is
          going. This test asks whether the reward pays for those fills.
        </p>
      </TestIntro>
      <Disclaimer
        summary="Paper only — not a recommendation."
        text="Paper only. Nothing on this page places an order. Rewards are estimated from Kalshi's published scoring rules and a once-a-minute view of the public order book; real rewards, queue position and fills would differ."
      />
      {query.isLoading && (
        <>
          <p className="visually-hidden" role="status">
            Loading Liquidity Rewards…
          </p>
          <Skeleton width="100%" height="8rem" />
          <Skeleton width="100%" height="14rem" />
        </>
      )}
      {query.isError && !data && <p role="alert">{error}</p>}
      {data && (
        <>
          {data.buckets.map((b) => (
            <Scorecard key={b.bucket} bucket={b} data={data} />
          ))}
          <Tracked rows={data.tracked} data={data} />
          <Recent markets={data.recent} />
          <p className={styles.fine}>
            Policy {data.policyId}: rest {data.policy.contractsPerSide} contracts on each
            side at Kalshi&apos;s Reference Price for that side (where resting size
            reaches a fifth of the program&apos;s target), re-posted every minute, only
            when both sides hold the target size so the minute can pay at all, between{' '}
            {priceCents(data.policy.minQuotePrice)} and{' '}
            {priceCents(data.policy.maxQuotePrice)}, never more than{' '}
            {data.policy.maxNetContracts} contracts net. Up to{' '}
            {data.policy.slotsPerBucket} markets per bucket, one per series, chosen by
            reward per hour, the strike nearest 50¢. Fills come from Kalshi&apos;s public
            trade tape: <em>optimistic</em> counts a trade at our price,{' '}
            <em>pessimistic</em> only a trade through it. A maker fee is charged on every
            fill as a conservative assumption. Inventory is valued at Kalshi&apos;s
            result, or at what selling into the bid would have fetched if no result comes
            within {Math.round(data.policy.settleWaitMs / 3_600_000)} hours. Registered{' '}
            {dayTime(Date.parse(data.registeredAt))}; only markets started after that
            count.{' '}
            {data.lastRunMs
              ? `Last run ${dayTime(data.lastRunMs)}.`
              : 'Waiting for the first run.'}
            {data.tapeTruncated > 0 &&
              ` ${data.tapeTruncated} minutes had more trades than were read.`}
            {data.lastFailures.length > 0 &&
              ` Last run: ${data.lastFailures.join(', ')}; it retries next minute.`}
          </p>
        </>
      )}
    </div>
  );
}

function Scorecard({
  bucket: b,
  data,
}: {
  bucket: LipBucketSummary;
  data: LiquidityRewardsResponse;
}) {
  const id = `lip-${b.bucket}-heading`;
  const has = b.programs > 0;
  return (
    <section className={styles.card} aria-labelledby={id}>
      <div className={styles.cardHead}>
        <h2 id={id}>{BUCKET_TITLE[b.bucket]}</h2>
        <span className={`${styles.verdict} ${styles[b.verdict]}`}>
          {VERDICT_LABEL[b.verdict]}
        </span>
      </div>
      <p className={styles.progress}>
        {b.programs}/{data.passBar.minPrograms} programs · {b.days}/{data.passBar.minDays}{' '}
        days · {b.quoting} quoting now
        {b.awaiting > 0 && `, ${b.awaiting} waiting for a result`}
      </p>
      {has && (
        <dl className={styles.stats}>
          <div>
            <dt>Net, pessimistic</dt>
            <dd className={tone(b.pessimisticNet)}>
              {formatSignedUsd(b.pessimisticNet)}
            </dd>
          </div>
          <div>
            <dt>Net, optimistic</dt>
            <dd className={tone(b.optimisticNet)}>{formatSignedUsd(b.optimisticNet)}</dd>
          </div>
          <div>
            <dt>A year on peak capital</dt>
            <dd className={tone(b.annualReturn)}>{pct(b.annualReturn)}</dd>
          </div>
        </dl>
      )}
      {has && (
        <p className={styles.small}>
          Pessimistic net splits into {formatSignedUsd(b.pessimisticReward)} of estimated
          rewards, {formatSignedUsd(b.pessimisticInventory)} on the contracts that filled
          ({b.pessimisticFills} fills) and {formatSignedUsd(-b.pessimisticFees)} of fees.
          Peak capital in use {usd(b.peakCapital)}.
          {b.dailyMean !== null &&
            ` Per day ${formatSignedUsd(b.dailyMean)}` +
              (b.dailyLow !== null && b.dailyHigh !== null
                ? ` (${formatSignedUsd(b.dailyLow)} to ${formatSignedUsd(b.dailyHigh)}).`
                : '.')}
          {b.halves &&
            ` First half ${formatSignedUsd(b.halves.first)}, second half ${formatSignedUsd(b.halves.second)}.`}
        </p>
      )}
      <p className={styles.small}>
        Passes after {data.passBar.minDays} days and {data.passBar.minPrograms} programs
        if pessimistic net is above zero in both halves, the daily range stays above zero,
        and the yearly return on peak capital beats{' '}
        {(data.passBar.minAnnualReturn * 100).toFixed(1)}%, about what Kalshi pays on idle
        cash.
      </p>
    </section>
  );
}

function Tracked({
  rows,
  data,
}: {
  rows: LiquidityTrackedRow[];
  data: LiquidityRewardsResponse;
}) {
  return (
    <section className={styles.card} aria-labelledby="lip-tracked-heading">
      <h2 id="lip-tracked-heading">Quoting now</h2>
      {rows.length === 0 ? (
        <p className={styles.small}>
          {Date.now() < Date.parse(data.registeredAt)
            ? `Starts ${dayTime(Date.parse(data.registeredAt))}. ${data.candidates.length} rewarded markets are lined up.`
            : 'Nothing right now; free slots fill from the next program refresh.'}
        </p>
      ) : (
        <ul className={styles.list}>
          {rows.map((t) => (
            <li key={t.ticker} className={styles.row}>
              <div className={styles.rowHead}>
                <strong>{t.ticker}</strong>
                <span className={tone(t.reward + t.liquidation)}>
                  {formatSignedUsd(t.reward + t.liquidation)}
                </span>
              </div>
              <p className={styles.small}>
                {t.phase === 'awaiting'
                  ? 'Waiting for the result'
                  : `Until ${dayTime(t.endMs)}`}{' '}
                · {usd(t.rewardDollars)} program ({usd(t.ratePerHour)}/hour) · reward so
                far {usd(t.reward)} · {t.fills} fills
                {t.net !== 0 &&
                  `, holding ${Math.abs(t.net)} ${t.net > 0 ? 'YES' : 'NO'}`}{' '}
                · paid {t.qualifyingSamples}/{t.samples} minutes
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Recent({ markets }: { markets: FinishedMarket[] }) {
  return (
    <section className={styles.card} aria-labelledby="lip-recent-heading">
      <h2 id="lip-recent-heading">Finished programs</h2>
      {markets.length === 0 ? (
        <p className={styles.small}>None yet.</p>
      ) : (
        <ul className={styles.list}>
          {markets.slice(0, 24).map((m) => (
            <li key={m.programId} className={styles.row}>
              <div className={styles.rowHead}>
                <strong>{m.ticker}</strong>
                <span className={tone(m.pessimistic.net)}>
                  {formatSignedUsd(m.pessimistic.net)}
                </span>
              </div>
              <p className={styles.small}>
                Ended {dayTime(m.endMs)} · reward {formatSignedUsd(m.pessimistic.reward)},
                inventory {formatSignedUsd(m.pessimistic.inventoryPnl)} (
                {m.valuation === 'settlement'
                  ? `settled ${m.result?.toUpperCase()}`
                  : 'sold into the bid'}
                ), {m.pessimistic.fills} fills · optimistic{' '}
                {formatSignedUsd(m.optimistic.net)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function tone(x: number | null) {
  if (x === null || Math.abs(x) < 1e-9) return styles.flat;
  return x > 0 ? styles.up : styles.down;
}
